<template>
  <div
    class="main-product critical-component-clear"
    :class="{ [$class('loading')]: state === 'loading' }"
  >
    <div class="grid">
      <div
        id="product-gallery"
        class="main-product__gallery-wrapper col xs-span l7"
        data-sign-post-location="main"
        js-sign-post
      >
        <product-gallery
          class="main-product__gallery"
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          layout="tabs-vertical"
          :loading="state === 'loading'"
          :product="productObject"
          :section="section"
          :variant="variant"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
        />
      </div>

      <div class="main-product__main col xs-span l5">
        <div class="main-product__details">
          <star-rating-link
            class="main-product__reviews"
            :product="productObject"
          />

          <product-badges
            class="main-product__badges"
            :loading="state === 'loading'"
            :product="productObject"
            template="product"
            :variant="variant"
            :bundle-disabled="bundleDisabled"
          />

          <h1
            id="template-title"
            class="main-product__title text-mobile-headlines-h3 text-desktop-headlines-h3"
            v-text="
              productObject.customTitle
                ? productObject.customTitle
                : productObject.title
            "
          />

          <p
            v-if="productObject.subtitle"
            class="main-product__subtitle text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"
            v-text="productObject.subtitle"
          />
        </div>

        <product-form
          class="main-product__form"
          :loading="state === 'loading'"
          :product="productObject"
          :section="section"
          :variant="variant"
          :bundle="bundle"
          location="PDP"
          @update-variant="handleVariantUpdate"
          @update-bundle-status="(value) => (bundleDisabled = value)"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Main product (main-product)
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {Object} liquid - Formatted product Liquid object.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Object} bundle - Canvas formatted bundle object.
 *
 * @emits updateRequestObject - Emit new product object.
 *
 */
import { mapActions, mapState } from 'pinia';
import useLastViewedProductStore from '~stores/last-viewed-products';

import ProductBadges from '~global/product-badges/product-badges';
import ProductForm from '~async/product-form/product-form';
import ProductGallery from '~async/product-gallery/product-gallery';
import StarRatingLink from '~global/star-rating/star-rating-link';

import productQuery from '~graphql/product.gql';
import { formatProduct } from '~/format/format-product';
import { formatTrackingItem } from '~/format/format-tracking-item';

import { computeDiscountLogic, emitUpdateRequestObject } from '~/helpers/vue';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

export default {
  name: 'MainProduct',

  components: {
    ProductBadges,
    ProductForm,
    ProductGallery,
    StarRatingLink,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
      }),
    },
    bundle: {
      type: [Object],
      default: () => ({}),
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    designMode: {
      type: Boolean,
      default: false,
    },
    section: {
      type: Object,
      default: () => ({
        id: '',
      }),
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
  },

  data() {
    return {
      bundleDisabled: false,
      currentIndex: 0,
      currentNamespace: 'gallery',
      handle: this.$variable('page.handle'),
      state: 'loading',
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
      discountCode: 'code',
    }),

    /**
     * Compute product object.
     * @returns {Object}
     */
    productObject() {
      if (this.state === 'loading' || !this.product) {
        return this.liquid;
      }

      if (
        (this.designMode && !this.product.handle) ||
        location.pathname.includes('products_preview')
      ) {
        return this.liquid;
      }

      return this.product;
    },
  },

  watch: {
    /**
     * Watch handle for update to load new product.
     */
    handle() {
      this.queryProduct(this.handle);
    },

    /**
     * Watch product for update to load new product.
     * @param {Object} newValue - New product.
     */
    product: {
      handler(newValue) {
        if (!newValue?.id) {
          return;
        }

        if (newValue.hiddenProduct?.value === 'true') {
          window.location.href = '/';
          return;
        }

        this.addViewedProduct(newValue.id);
        this.queryProduct(this.handle);
      },
      immediate: true,
    },
  },

  mounted() {
    this.$emit('update-request-object', {
      response: this.liquid,
      type: 'product',
    });

    this.queryProduct();
  },

  methods: {
    /**
     * Map Pinia actions.
     */
    ...mapActions(useLastViewedProductStore, ['addViewedProduct']),

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false || this.product.handle === handle) {
        return;
      }

      this.state = 'loading';

      try {
        const response = await cnvs.Query({
          query: productQuery,
          variables: {
            country: this.$variable('store.country'),
            handle,
            language: this.$variable('store.language'),
          },
        });

        if (this.$variable('settings.disableComponentReadyState')) {
          return;
        }

        const formattedProduct = formatProduct(response);

        this.$emit('update-request-object', {
          response: formattedProduct,
          type: 'product',
        });

        this.updateProductUrl(formattedProduct.handle);
        this.state = 'ready';
      } catch (error) {
        cnvs.ReportError('Failed to load product', error);
      }
    },

    /**
     * Update product URL when loading new handle.
     * - Replaces state so it doesn't affect back navigation.
     * - Use pushState instead to affect back navigation.
     * - Maintains /collections/ URL.
     * @param {String} handle - New product handle.
     */
    updateProductUrl(handle) {
      const oldHandle = location.pathname.split('/').reverse()[0];

      if (!oldHandle || oldHandle === handle) {
        return;
      }

      const pathname = location.pathname.replace(oldHandle, handle);
      let url = `${location.origin}${pathname}`;

      /** for preview mode we need to have a different url structure */
      if (window.location.pathname.includes('products_preview')) {
        url = `${location.origin}${location.pathname}${location.search}`;
      }

      history.replaceState(
        {
          html: '',
        },
        '',
        url,
      );
    },

    /**
     * Update current carousel index.
     * @param {Number} newIndex - New carousel index.
     */
    handleUpdateCurrentIndex(newIndex) {
      if (newIndex === this.currentIndex) {
        return;
      }

      this.currentIndex = newIndex;
    },

    /**
     * Update current carousel namespace.
     * @param {String} newNamespace - New carousel namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.currentNamespace = newNamespace;
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.handleUpdateCurrentIndex(0);

      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      });

      const params = new URLSearchParams(document.location.search.substring(1));

      if (variant?.id) {
        params.set('variant', variant.id);
      }

      const url = variant?.id
        ? `${location.origin}${location.pathname}?${params.toString()}`
        : `${location.origin}${location.pathname}`;

      history.replaceState(
        {
          html: '',
        },
        '',
        url,
      );

      // Tracking event view item.
      this.handleTrackingViewItem(variant);

      // meta tag update
      this.updateMetaTags(variant);
    },

    /**
     * Tracking event view item.
     */
    handleTrackingViewItem(variant) {
      // Get available variant
      let selectedVariant = this.product.selectedOrFirstAvailableVariant;
      if (variant) {
        selectedVariant = variant;
      }
      const price = selectedVariant.price?.amount;
      const compareAtPrice = selectedVariant.compare_at_price?.amount;
      const discountLogic = computeDiscountLogic({
        discountPreview: false,
        onSale: compareAtPrice > price,
        product: this.product,
        variant: selectedVariant,
      });
      const percentageDiscount = this.getCampaignDiscount(
        discountLogic,
        'active',
      );
      const discountObject = {
        percentageDiscount,
        discountCode: this.discountCode,
      };
      const deliveryTime = this.$string('product.delivery_time', {
        replace: { time: this.$variable('product.deliveryTime') },
      });
      const currencyCode = this.$variable('store.currency');
      const viewItemValue = selectedVariant.price?.amount / 100;

      const item = formatTrackingItem({
        product: this.product,
        variant: selectedVariant,
        template: 'PDP',
        discountObject,
        deliveryTime,
        currencyCode,
        quantity: 1,
      });

      cnvs.EventBus.emit('dataLayer:view_item', {
        event: 'view_item',
        currency: currencyCode,
        value: viewItemValue,
        items: [item],
      });
    },

    /**
     * Update meta tags for social share with variant data.
     */
    updateMetaTags(variant) {
      const ogImage = document.querySelector('meta[property="og:image"]');
      const ogImageAlt = document.querySelector(
        'meta[property="og:image:alt"]',
      );
      const ogImageHeight = document.querySelector(
        'meta[property="og:image:height"]',
      );
      const ogImageWidth = document.querySelector(
        'meta[property="og:image:width"]',
      );
      const ogSecureUrl = document.querySelector(
        'meta[property="og:image:secure_url"]',
      );
      const twitterImage = document.querySelector('meta[name="twitter:image"]');

      if (ogImage) {
        ogImage.setAttribute('content', variant.featuredImage?.src);
      }

      if (ogImageAlt) {
        ogImageAlt.setAttribute('content', variant.featuredImage?.alt);
      }

      if (ogImageHeight) {
        ogImageHeight.setAttribute('content', variant.featuredImage?.height);
      }

      if (ogImageWidth) {
        ogImageWidth.setAttribute('content', variant.featuredImage?.width);
      }

      if (ogSecureUrl) {
        ogSecureUrl.setAttribute('content', window.location.href);
      }

      if (twitterImage) {
        twitterImage.setAttribute('content', variant.featuredImage?.src);
      }
    },
  },
};
</script>

<style lang="scss">
@import '~async/product-gallery-modal/product-gallery-modal-overlay';
@import './main-product';
</style>
